'use client';

import React, { useState } from 'react';
import { X, Search, Clock, Plus, CheckCircle2, AlertCircle, User, ShieldAlert } from 'lucide-react';
import { Staff, ServiceItem } from './types';

interface NewBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  staffList: Staff[];
  services: ServiceItem[];
  selectedDate: string;
  onSuccess: () => void;
}

export const NewBookingModal: React.FC<NewBookingModalProps> = ({
  isOpen,
  onClose,
  staffList,
  services,
  selectedDate,
  onSuccess,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedServiceId, setSelectedServiceId] = useState<string>(
    services[0]?.id || ''
  );
  const [selectedStaffId, setSelectedStaffId] = useState<string>(
    staffList[0]?.id || 'any'
  );
  const [bookingDate, setBookingDate] = useState(selectedDate);
  const [bookingTime, setBookingTime] = useState('11:00');
  const [clientName, setClientName] = useState('Cliente Mostrador');
  const [clientPhone, setClientPhone] = useState('1199998888');
  const [isOverturn, setIsOverturn] = useState(false);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  if (!isOpen) return null;

  const filteredServices = services.filter((s) =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedService = services.find((s) => s.id === selectedServiceId) || services[0];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    setSubmitting(true);

    try {
      const res = await fetch('/api/bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          serviceId: selectedServiceId,
          staffId: selectedStaffId,
          date: bookingDate,
          time: bookingTime,
          clientName: clientName.trim(),
          clientPhone: clientPhone.trim(),
          isOverturn,
          notes: notes.trim(),
        }),
      });

      const data = await res.json();

      if (data.success) {
        onSuccess();
        onClose();
      } else {
        if (data.code === 'SLOT_NOT_AVAILABLE') {
          setErrorMessage(
            'El horario ya se encuentra ocupado. Puedes marcar la casilla "Forzar como Sobreturno" abajo para agendar de todos modos.'
          );
        } else {
          setErrorMessage(data.error || 'No se pudo crear la reserva.');
        }
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Error de conexión.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl border border-slate-100 overflow-hidden animate-fade-in">
        {/* Cabecera */}
        <div className="p-5 border-b border-slate-200 flex items-center justify-between bg-slate-50/60">
          <h2 className="text-lg font-black text-[#0f172a]">Nuevo turno (POS)</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-200 hover:bg-slate-300 flex items-center justify-center text-slate-600 transition cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 flex flex-col gap-5">
          {errorMessage && (
            <div className="p-3.5 rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold flex items-start gap-2.5">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-rose-500" />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Buscador de Servicio */}
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">
              Servicio a Agendar
            </label>
            <div className="flex items-center gap-2 border border-slate-200 rounded-xl px-3 py-2 bg-slate-50 mb-2">
              <Search className="w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar servicio (ej. Corte, Lavado)..."
                className="w-full text-xs font-semibold text-[#0f172a] bg-transparent focus:outline-none"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-36 overflow-y-auto pr-1">
              {filteredServices.map((srv) => (
                <div
                  key={srv.id}
                  onClick={() => setSelectedServiceId(srv.id)}
                  className={`p-2.5 rounded-xl border text-xs cursor-pointer transition flex items-center justify-between ${
                    selectedServiceId === srv.id
                      ? 'border-[#047857] bg-emerald-50/70 font-bold text-[#047857]'
                      : 'border-slate-200 hover:bg-slate-50 text-slate-700'
                  }`}
                >
                  <span className="truncate">{srv.name}</span>
                  <span className="shrink-0 font-extrabold ml-2">
                    ${' '}
                    {srv.cashPrice?.toLocaleString('es-AR', {
                      minimumFractionDigits: 0,
                    })}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Profesional, Fecha y Hora */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                Profesional
              </label>
              <select
                value={selectedStaffId}
                onChange={(e) => setSelectedStaffId(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-[#0f172a] focus:outline-none"
              >
                <option value="any">Cualquiera disponible</option>
                {staffList.map((st) => (
                  <option key={st.id} value={st.id}>
                    {st.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                Fecha
              </label>
              <input
                type="date"
                required
                value={bookingDate}
                onChange={(e) => setBookingDate(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-[#0f172a] focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                Hora
              </label>
              <input
                type="time"
                required
                value={bookingTime}
                onChange={(e) => setBookingTime(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-[#0f172a] focus:outline-none"
              />
            </div>
          </div>

          {/* Datos del Cliente */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                Nombre del Cliente
              </label>
              <input
                type="text"
                required
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Nombre y apellido"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-[#0f172a] focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                Teléfono / WhatsApp
              </label>
              <input
                type="text"
                required
                value={clientPhone}
                onChange={(e) => setClientPhone(e.target.value)}
                placeholder="Ej: 1123456789"
                className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-[#0f172a] focus:outline-none"
              />
            </div>
          </div>

          {/* Casilla Sobreturno Forzado */}
          <div className="bg-amber-50/70 border border-amber-200 rounded-2xl p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-amber-700" />
              <span className="text-xs font-bold text-amber-900">
                Forzar como Sobreturno
              </span>
            </div>
            <input
              type="checkbox"
              checked={isOverturn}
              onChange={(e) => setIsOverturn(e.target.checked)}
              className="w-4 h-4 accent-amber-600 rounded cursor-pointer"
            />
          </div>

          {/* Notas */}
          <div>
            <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
              Notas internas (opcional)
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observaciones de mostrador..."
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-semibold text-[#0f172a] focus:outline-none"
            />
          </div>

          {/* Barra Flotante Inferior estilo Mostrador POS (AppGenda Fase 3) */}
          <div className="bg-slate-900 text-white rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-3 mt-2 shadow-lg">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
                <User className="w-4 h-4" />
              </div>
              <div className="text-left">
                <span className="block text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                  AGENDANDO PARA
                </span>
                <span className="block text-xs font-bold text-white">
                  {clientName || 'Cliente'} ({clientPhone || 'Sin tel'})
                </span>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto bg-[#10b981] hover:bg-[#059669] text-white px-5 py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition cursor-pointer shadow-md"
            >
              {submitting ? 'Agendando...' : `Confirmar Turno ($${selectedService?.cashPrice || 0})`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
